import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import '../../styles/aida-base.css';
import '../../styles/aida-responsive.css';

/**
 * RWBYNewsAnnouncement Page
 * Launch announcement: Playcast x FarBridge — RWBY: Grimm Eclipse.
 * Route: /news/rwby-grimm-eclipse-launch
 */

const ARTICLE_URL = 'https://playcast.io/news/rwby-grimm-eclipse-launch';
const HERO_IMAGE = 'https://playcast.io/img/aida/RWBYHeader.png';
const LOGO_URL = 'https://playcast.io/logo.svg';
const DEMO_URL = 'https://demo.playcast.io/q/rwby_car';
const DATE_PUBLISHED = '2026-04-24T10:00:00Z';

const NEWS_ARTICLE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'NewsArticle',
  headline: 'Playcast x FarBridge Launch RWBY: Grimm Eclipse on Mobile & Social',
  description:
    'RWBY: Grimm Eclipse is the first launch title on Playcast\'s instant-play platform — play the full PC co-op game on iOS, Android, Discord, Twitch, Instagram, TikTok, and YouTube. No download, no install.',
  image: [HERO_IMAGE],
  datePublished: DATE_PUBLISHED,
  dateModified: DATE_PUBLISHED,
  author: {
    '@type': 'Organization',
    name: 'Playcast',
    url: 'https://playcast.io',
  },
  publisher: {
    '@type': 'Organization',
    name: 'Playcast',
    url: 'https://playcast.io',
    logo: {
      '@type': 'ImageObject',
      url: LOGO_URL,
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': ARTICLE_URL,
  },
};

const VIDEO_GAME_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'VideoGame',
  name: 'RWBY: Grimm Eclipse',
  url: DEMO_URL,
  description:
    'RWBY: Grimm Eclipse is a 4-player co-op action game based on the Rooster Teeth animated series. Play the full PC version instantly on any device — no download, no install, no app store required.',
  image: HERO_IMAGE,
  gamePlatform: ['PC', 'iOS', 'Android', 'Web Browser', 'Discord', 'Twitch', 'Instagram', 'TikTok', 'YouTube'],
  applicationCategory: 'Game',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
  },
  developer: {
    '@type': 'Organization',
    name: 'FarBridge',
    url: 'https://farbridge.com',
  },
  publisher: {
    '@type': 'Organization',
    name: 'Playcast',
    url: 'https://playcast.io',
  },
};

const ORGANIZATION_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Playcast',
  url: 'https://playcast.io',
  logo: LOGO_URL,
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'media@playcast.io',
    contactType: 'Press',
  },
  sameAs: [
    'https://www.linkedin.com/company/playcastdotio',
    'https://www.instagram.com/playcastdotio',
    'https://www.youtube.com/@playcast',
    'https://www.tiktok.com/@playcast',
    'https://x.com/playcastdotio',
  ],
};

const PURPLE_GRADIENT = [
  'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 10%, rgba(0,0,0,0.2) 25%, rgba(0,0,0,0.0) 40%, rgba(0,0,0,0.0) 60%, rgba(0,0,0,0.2) 75%, rgba(0,0,0,0.85) 90%, rgba(0,0,0,1) 100%)',
  'linear-gradient(90deg, rgba(120,40,255,0.55), rgba(180,60,255,0.55))',
  'radial-gradient(1400px at 50% 40%, rgba(255,255,255,0.03), rgba(60,0,90,0) 75%)',
  'radial-gradient(1000px at 15% 35%, rgba(140,40,255,0.40), rgba(40,0,70,0) 75%)',
  'radial-gradient(900px at 55% 60%, rgba(160,50,255,0.30), rgba(35,0,60,0) 80%)',
  'radial-gradient(1000px at 85% 70%, rgba(200,70,255,0.40), rgba(45,0,70,0) 80%)',
  'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.00) 60%)',
  'radial-gradient(circle at 50% 25%, #2a0050 0%, #1a0038 35%, #000000 75%, #000000 100%)',
].join(', ');

const PURPLE_BLEND = 'normal, soft-light, overlay, screen, screen, screen, soft-light, multiply';

const sectionHeading: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 900,
  fontStyle: 'italic',
  fontSize: '36px',
  lineHeight: '46px',
  letterSpacing: '-0.3px',
  color: '#FBBF24',
  margin: '56px 0 20px',
  fontVariationSettings: "'wght' 900",
};

const subHeading: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '24px',
  lineHeight: '34px',
  color: 'rgba(255,255,255,0.95)',
  margin: '32px 0 12px',
};

const paragraph: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '20px',
  lineHeight: '34px',
  color: 'rgba(255,255,255,0.85)',
  margin: '0 0 20px',
};

const bulletList: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '20px',
  lineHeight: '34px',
  color: 'rgba(255,255,255,0.85)',
  margin: '12px 0 20px',
  paddingLeft: '32px',
  listStyleType: 'disc',
};

const bulletItem: React.CSSProperties = {
  margin: '6px 0',
};

const blockquote: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontStyle: 'italic',
  fontSize: '20px',
  lineHeight: '34px',
  color: 'rgba(255,255,255,0.9)',
  margin: '32px 0',
  paddingLeft: '24px',
  borderLeft: '3px solid #FBBF24',
};

const quoteAttribution: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontStyle: 'normal',
  fontSize: '16px',
  lineHeight: '24px',
  color: 'rgba(255,255,255,0.6)',
  marginTop: '8px',
};

export const RWBYNewsAnnouncement = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Playcast x FarBridge Launch RWBY: Grimm Eclipse on Mobile &amp; Social | Playcast</title>
        <meta
          name="description"
          content="RWBY: Grimm Eclipse is the first launch title on Playcast's instant-play platform — play the full PC co-op game on iOS, Android, Discord, Twitch, Instagram, TikTok, and YouTube. No download, no install."
        />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Playcast x FarBridge Launch RWBY: Grimm Eclipse on Mobile & Social" />
        <meta
          property="og:description"
          content="RWBY: Grimm Eclipse is the first launch title on Playcast's instant-play platform — play the full PC co-op game on iOS, Android, Discord, Twitch, Instagram, TikTok, and YouTube. No download, no install."
        />
        <meta property="og:image" content={HERO_IMAGE} />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={HERO_IMAGE} />
        <script type="application/ld+json">{JSON.stringify(NEWS_ARTICLE_JSONLD)}</script>
        <script type="application/ld+json">{JSON.stringify(VIDEO_GAME_JSONLD)}</script>
        <script type="application/ld+json">{JSON.stringify(ORGANIZATION_JSONLD)}</script>
      </Helmet>

      <div
        className="w-full"
        style={{
          margin: 0,
          padding: 0,
          width: '100%',
          minHeight: '100vh',
          overflow: 'hidden',
          background: PURPLE_GRADIENT,
          backgroundBlendMode: PURPLE_BLEND,
        }}
      >
        <div style={{ paddingTop: '120px', display: 'flex', justifyContent: 'center' }}>
          <article style={{ width: '100%', maxWidth: '820px', padding: '40px 24px 80px' }}>

            {/* Hero Image */}
            <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginBottom: '48px' }}>
              <img
                src="/img/aida/RWBYHeader.png"
                alt="RWBY: Grimm Eclipse on Playcast"
                style={{
                  width: '100%',
                  borderRadius: '16px',
                  display: 'block',
                  cursor: 'pointer',
                }}
              />
            </a>

            {/* Dateline */}
            <p style={{ ...paragraph, fontSize: '14px', lineHeight: '20px', opacity: 0.5, margin: '0 0 32px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              Everett, WA — April 24, 2026
            </p>

            {/* Title */}
            <h1 style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 900,
              fontSize: '48px',
              lineHeight: '58px',
              letterSpacing: '-0.5px',
              color: '#fff',
              margin: '0 0 48px',
              fontVariationSettings: "'wght' 900",
            }}>
              Playcast and FarBridge Bring <span style={{ color: '#FBBF24' }}>RWBY: Grimm Eclipse</span> to Mobile and Social — With Zero Developer Work
            </h1>

            <p style={{ ...paragraph, fontSize: '22px', lineHeight: '36px', fontStyle: 'italic' }}>
              Starting today, anyone with a phone, a browser, or a social media account can tap once and be inside <strong>RWBY: Grimm Eclipse</strong> — the full PC co-op game — in seconds. No download. No install. No app store. No account required.
            </p>

            <p style={paragraph}>
              Playcast Inc. and Austin-based studio <strong>FarBridge</strong> today announced a launch partnership that makes RWBY: Grimm Eclipse the first title on Playcast's instant-play platform. The game is available now across iOS, Android, Instagram, TikTok, YouTube, Discord, and Twitch.
            </p>

            <p style={paragraph}>
              FarBridge shipped this without building a separate mobile version. The same PC build that runs on Steam now streams to every device through Playcast's network of community hosts — unmodified, with touch controls layered on top.
            </p>

            {/* Play Now CTA */}
            <div style={{
              background: 'rgba(251,191,36,0.08)',
              border: '1px solid rgba(251,191,36,0.3)',
              borderRadius: '12px',
              padding: '28px 32px',
              margin: '40px 0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              textAlign: 'center',
            }}>
              <p style={{ ...paragraph, margin: 0, fontWeight: 600, color: '#fff' }}>
                Try it right now — no download needed.
              </p>
              <a
                href={DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  background: '#FBBF24',
                  color: '#000',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '18px',
                  lineHeight: '1',
                  padding: '14px 32px',
                  borderRadius: '44px',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                Play RWBY: Grimm Eclipse
              </a>
            </div>

            <h2 style={sectionHeading}>A New Path from PC to Every Platform</h2>
            <p style={paragraph}>
              For most studios, reaching mobile from a finished PC game means 18 to 24 months of porting work and several million dollars in engineering. Playcast removes that math entirely. A game that ships on Steam can reach every iPhone, every Android device, and every major social platform the same day — using the same build.
            </p>
            <p style={paragraph}>
              Playcast streams PC games directly to browsers and social surfaces through a distributed network of community hosts. Each player request is matched with a nearby available machine in real time, delivering low-latency play at a fraction of traditional cloud gaming costs.
            </p>

            <blockquote style={blockquote}>
              "Every PC publisher we talk to wants the mobile and social audience. None of them want to spend two years and seven figures rebuilding their game to get it. Playcast removes that math entirely. FarBridge handed us a finished PC build. We handed them iOS, Android, and every major social platform. That is the new pipeline — and the playable becomes the ad."
              <p style={quoteAttribution}>— Alex St. John, Founder &amp; CEO, Playcast</p>
            </blockquote>

            <blockquote style={blockquote}>
              "Playcast let us put the full PC version of RWBY: Grimm Eclipse in front of a mobile and social audience without compromising the game and without diverting the team. No mobile port, no extra integration work. That is a genuinely new option for our industry."
              <p style={quoteAttribution}>— Patrick Curry, CEO, FarBridge Games</p>
            </blockquote>

            <h2 style={sectionHeading}>Playable Ads and a New Way to Find Players</h2>
            <p style={paragraph}>
              Playcast rewires user acquisition by collapsing the gap between discovery and play. A scroller taps an ad, a Discord Activity, or a creator link and is immediately inside the game — streaming in their browser, no handoff, no install-attribution decay, no funnel drop.
            </p>
            <p style={paragraph}>
              Publishers can configure time-boxed trials, event-driven activations, and reward-based unlocks from the web — giving them marketing levers they have not had before. For platform operators like Discord and Twitch, every channel and every stream becomes a launch surface for the games being discussed there.
            </p>

            <h2 style={sectionHeading}>About RWBY: Grimm Eclipse</h2>
            <p style={paragraph}>
              RWBY: Grimm Eclipse is a 4-player co-op action game developed by FarBridge and based on the hit animated series by Rooster Teeth. Players choose from Ruby, Weiss, Blake, or Yang and battle waves of Grimm across locations from the show. It's built for playing with friends, fast to pick up, and available today through Playcast.
            </p>
            <ul style={bulletList}>
              <li style={bulletItem}>Available on iOS, Android, Instagram, TikTok, YouTube, Discord, and Twitch</li>
              <li style={bulletItem}>Full PC version — no mobile compromise</li>
              <li style={bulletItem}>Solo or 4-player co-op</li>
              <li style={bulletItem}>Full controller support</li>
              <li style={bulletItem}>No download, no install, no account required to play</li>
            </ul>

            <h2 style={sectionHeading}>About Playcast</h2>
            <p style={paragraph}>
              Playcast is the platform for instant-play PC games on any device. Playcast streams PC experiences directly to browsers and social surfaces — no downloads, no installs, no proprietary client — and is designed to complement publishers' existing storefronts, accounts, and commerce. Powered by a distributed network of community hosts, Playcast gives publishers a new channel to reach, activate, and retain players across TikTok, Instagram, Discord, YouTube, and Twitch.
            </p>

            <h2 style={sectionHeading}>About FarBridge</h2>
            <p style={paragraph}>
              Based in Austin, Texas, FarBridge is a premier developer specializing in bringing high-end gaming experiences to new platforms. From VR to mobile, FarBridge builds worlds that players want to stay in.
            </p>

            {/* Media contacts */}
            <div style={{ marginTop: '56px', paddingTop: '32px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <h3 style={subHeading}>Media Contacts</h3>
              <p style={{ ...paragraph, margin: '0' }}>Playcast Media Relations — <a href="mailto:media@playcast.io" style={{ color: '#FBBF24' }}>media@playcast.io</a></p>
              <p style={{ ...paragraph, margin: '0' }}>FarBridge Communications — <a href="mailto:press@farbridge.com" style={{ color: '#FBBF24' }}>press@farbridge.com</a></p>
            </div>

          </article>
        </div>
      </div>
    </>
  );
};
